import { useMemo, useState, useEffect } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, FileDown } from 'lucide-react';
import api from '../lib/api';
import { Invitation } from '../types';
import Input from './ui/Input';
import Button from './ui/Button';

function SortRow({
  inv,
  onNoteBlur,
}: {
  inv: Invitation;
  onNoteBlur: (id: string, note: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: inv.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.85 : 1,
  };
  const [note, setNote] = useState(inv.shortlistNote || '');

  useEffect(() => {
    setNote(inv.shortlistNote || '');
  }, [inv.shortlistNote]);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-start gap-3 p-3 bg-glass-2 border border-glass rounded-xl hover:bg-glass-3 transition-colors"
    >
      <button type="button" className="mt-1 text-glass-text-3 hover:text-glass-text cursor-grab" {...attributes} {...listeners}>
        <GripVertical className="w-5 h-5" />
      </button>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-glass-text">{inv.kolProfile?.name}</div>
        <Input
          className="mt-2 text-sm"
          placeholder="Note for this KOL..."
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onBlur={() => onNoteBlur(inv.id, note)}
        />
      </div>
    </div>
  );
}

export default function ShortlistRanker({
  jobId,
  invitations,
  onUpdated,
}: {
  jobId: string;
  invitations: Invitation[];
  onUpdated: () => void;
}) {
  const sorted = useMemo(() => {
    return [...invitations].sort((a, b) => {
      const ra = a.shortlistRank ?? 9999;
      const rb = b.shortlistRank ?? 9999;
      if (ra !== rb) return ra - rb;
      return (a.kolProfile?.name || '').localeCompare(b.kolProfile?.name || '');
    });
  }, [invitations]);

  const [items, setItems] = useState<Invitation[]>(sorted);

  useEffect(() => {
    setItems(sorted);
  }, [sorted]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const persistRanks = async (ordered: Invitation[]) => {
    for (let i = 0; i < ordered.length; i++) {
      const inv = ordered[i];
      await api.patch(`/invitations/${inv.id}/shortlist-meta`, { shortlistRank: i + 1 });
    }
    onUpdated();
  };

  const handleDragEnd = async (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((x) => x.id === active.id);
    const newIndex = items.findIndex((x) => x.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(items, oldIndex, newIndex);
    setItems(next);
    await persistRanks(next);
  };

  const exportCsv = () => {
    const lines = ['rank,name,email,note'];
    items.forEach((inv, i) => {
      const name = (inv.kolProfile?.name || '').replace(/"/g, '""');
      const email = (inv.kolProfile?.email || '').replace(/"/g, '""');
      const note = (inv.shortlistNote || '').replace(/"/g, '""');
      lines.push(`${i + 1},"${name}","${email}","${note}"`);
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'shortlist.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/invitations/jobs/${jobId}/shortlist.pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'shortlist.pdf';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      alert('Could not download PDF');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" size="sm" onClick={downloadPdf}>
          <FileDown className="w-4 h-4 mr-1 inline" />
          Export PDF
        </Button>
        <Button type="button" variant="outline" size="sm" onClick={exportCsv}>
          Export shortlist CSV
        </Button>
      </div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {items.map((inv) => (
              <SortRow
                key={inv.id}
                inv={inv}
                onNoteBlur={async (id, note) => {
                  await api.patch(`/invitations/${id}/shortlist-meta`, { shortlistNote: note });
                  onUpdated();
                }}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}
